/**
 * CampaignsTab — Campaign cards for a business.
 *
 * Shows campaign status, spend progress, dates. Click navigates to CampaignStatus.
 */
import React from 'react';
import {
  BarChart3, DollarSign, Users, Calendar,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDollars } from '@/components/deal/mockDealData';

const STATUS_BADGE = {
  live: { label: 'Live', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  paused: { label: 'Paused', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export default function CampaignsTab({ campaigns = [], onViewCampaign }) {
  if (campaigns.length === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="No active campaigns"
        description="Complete the deal flow to launch your first campaign."
      />
    );
  }

  return (
    <div className="space-y-3">
      {campaigns.map((campaign) => {
        const spendPercent = Math.round((campaign.spend / campaign.budget_cap) * 100);
        const statusConfig = STATUS_BADGE[campaign.status] || STATUS_BADGE.live;

        return (
          <Card
            key={campaign.id}
            className="cursor-pointer hover:border-coral/40 hover:shadow-md transition-all"
            onClick={() => onViewCampaign?.(campaign)}
          >
            <CardContent className="p-4 space-y-3">
              {/* Header: name + status */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">{campaign.name}</h3>
                <Badge variant="outline" className={`text-[10px] ${statusConfig.className}`}>
                  {statusConfig.label}
                </Badge>
              </div>

              {/* Metrics row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <Users className="h-3.5 w-3.5 text-coral mx-auto mb-0.5" />
                  <p className="text-sm font-bold text-foreground">{campaign.households_reached?.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">Households</p>
                </div>
                <div className="text-center">
                  <DollarSign className="h-3.5 w-3.5 text-emerald-600 mx-auto mb-0.5" />
                  <p className="text-sm font-bold text-foreground">{formatDollars(campaign.spend)}</p>
                  <p className="text-[10px] text-muted-foreground">Spend</p>
                </div>
                <div className="text-center">
                  <Calendar className="h-3.5 w-3.5 text-purple-600 mx-auto mb-0.5" />
                  <p className="text-sm font-bold text-foreground">{campaign.days_remaining}</p>
                  <p className="text-[10px] text-muted-foreground">Days left</p>
                </div>
              </div>

              {/* Spend progress */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{spendPercent}% of budget</span>
                  <span>{formatDollars(campaign.spend)} / {formatDollars(campaign.budget_cap)}</span>
                </div>
                <Progress
                  value={spendPercent}
                  className="h-1.5 [&>[data-slot=progress-indicator]]:bg-coral"
                />
              </div>

              {/* Dates */}
              <p className="text-[10px] text-muted-foreground">
                {campaign.start_date} — {campaign.end_date}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
